import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Box,
  Skeleton,
  Card,
  CardContent,
  Avatar,
  Button,
  useTheme,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import { useParams, useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../../styles/projects.css";
import CyberIssueList from "./CyberIssueList";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CybersecurityTesting = () => {
  const { projectId } = useParams();
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const issueSelectionRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/flawfinder/flawfinder-cybersecurity-analysis-result?project_id=${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();

        // Handle no results case
        if (data?.message === "No cybersecurity analysis found in the latest run.") {
          setChartData(null); // Will trigger "No chart data available."
          return;
        }
        const result = data?.flawfinderResults?.[0];

        if (!result) {
          setChartData(null);
          return;
        }

        let info = 0,
          low = 0,
          medium = 0,
          major = 0,
          critical = 0,
          blocker = 0;

        const vulnerabilities = result?.vulnerabilities || [];
        vulnerabilities.forEach((vuln) => {
          const level = vuln?.Level?.trim();
          switch (level) {
            case "0":
              info++;
              break;
            case "1":
              low++;
              break;
            case "2":
              medium++;
              break;
            case "3":
              major++;
              break;
            case "4":
              critical++;
              break;
            case "5":
              blocker++;
              break;
            default:
              break;
          }
        });

        setChartData({ info, low, medium, major, critical, blocker });
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setChartData(null);
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [projectId]);

  const IssuesBarChart = () => {
    if (chartLoading) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 1 }} />
          <Typography variant="body2" color="textSecondary">
            Loading chart data...
          </Typography>
        </Box>
      );
    }
    if (!chartData)
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" color="textSecondary">
            No chart data available.
          </Typography>
        </Box>
      );

    const data = {
      labels: ["INFO", "LOW", "MEDIUM", "MAJOR", "CRITICAL", "BLOCKER"],
      datasets: [
        {
          label: "Severity Levels",
          data: [
            chartData.info,
            chartData.low,
            chartData.medium,
            chartData.major,
            chartData.critical,
            chartData.blocker,
          ],
          backgroundColor: [
            "#17A2B8", // INFO
            "#28A745", // LOW
            "#FFC107", // MEDIUM
            "#FD7E14", // MAJOR
            "#DC3545", // CRITICAL
            "#343A40", // BLOCKER
          ],
        },
      ],
    };

    const severityLevelMap = {
      INFO: 0,
      LOW: 1,
      MEDIUM: 2,
      MAJOR: 3,
      CRITICAL: 4,
      BLOCKER: 5,
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (context) {
              const label = context.label || "";
              const levelValue = severityLevelMap[label] ?? "N/A";
              const count = context.parsed.y ?? context.parsed;
              return `Severity Level: ${label} (Level ${levelValue}) — Count: ${count}`;
            },
          },
        },
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const clickedIndex = elements[0].index;
          const severityLevel = data.labels[clickedIndex];
          setSelectedSeverity(severityLevel);
          if (issueSelectionRef.current) {
            issueSelectionRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }
      },
    };

    return (
      <Box sx={{ height: 300 }}>
        <Bar data={data} options={options} />
      </Box>
    );
  };

  const IssuesDetails = () => {
    const severityLevels = [
      {
        label: "INFO",
        count: chartData?.info || 0,
        gradient: "linear-gradient(135deg, #17A2B8, #48C6EF)",
      },
      {
        label: "LOW",
        count: chartData?.low || 0,
        gradient: "linear-gradient(135deg, #28A745, #6FCF97)",
      },
      {
        label: "MEDIUM",
        count: chartData?.medium || 0,
        gradient: "linear-gradient(135deg, #FFC107, #FFD700)",
      },
      {
        label: "MAJOR",
        count: chartData?.major || 0,
        gradient: "linear-gradient(135deg, #FD7E14, #FFB347)",
      },
      {
        label: "CRITICAL",
        count: chartData?.critical || 0,
        gradient: "linear-gradient(135deg, #DC3545, #FF6B6B)",
      },
      {
        label: "BLOCKER",
        count: chartData?.blocker || 0,
        gradient: "linear-gradient(135deg, #343A40, #5A5A5A)",
      },
    ];

    return (
      <Grid container spacing={2}>
        {severityLevels.map(({ label, count, gradient }) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card
              sx={{
                background: gradient,
                color: "#fff",
                textAlign: "center",
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "120px",
                borderRadius: 2,
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
              onClick={() => {
                setSelectedSeverity(label.toLowerCase());
                if (issueSelectionRef.current) {
                  issueSelectionRef.current.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <CardContent sx={{ p: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                      width: 30,
                      height: 30,
                      mr: 1,
                      ml: 0,
                    }}
                  >
                    {label.charAt(0)}
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: 15, fontWeight: "bold" }}
                  >
                    {label}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontSize: 24 }}>
                  {count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box className="static-analysis-container" sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mt: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">
          Security Vulnerabilities by Severity
        </Typography>
        <Button
          variant="contained"
          className="athrv-dash-primary-button"
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? "#263d68" : undefined,
          }}
          onClick={() => navigate("/projects")}
        >
          Back to Project List
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <IssuesBarChart />
        </Grid>
        <Grid item xs={12} md={6}>
          <IssuesDetails />
        </Grid>
      </Grid>

      <div ref={issueSelectionRef}>
        {chartData && (
          <CyberIssueList
            projectId={projectId}
            selectedSeverity={selectedSeverity}
          />
        )}
      </div>
    </Box>
  );
};

export default CybersecurityTesting;